import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import { useRecurly, CardElementChangeEvent } from '@recurly/react-recurly';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { UseMutationResult } from '@tanstack/react-query';
import * as Sentry from '@sentry/capacitor';
import { TFunction } from 'i18next';
import {
  DonationCheckoutConfig,
  MembershipCheckoutConfig,
  ProcessDonationPaymentTokenData,
  ProcessInitialDonationPaymentTokenData,
  ProcessPaymentState,
  ProcessMembershipPaymentTokenData,
  SavePaymentMethodData,
} from 'shared/types';
import { formatToUSDollars } from 'shared/utils';
import { parseRecurlyErrorToTransKey } from 'features/payments/payments.utils';
import { Loader } from 'components/Loader';
import { RecurlyCardElement } from './RecurlyCardElement';
import { getButtonTitle, getCreditCardValidationErrors } from './utils';
import { CheckoutPriceSummary } from '../CheckoutPriceSummary';

type RecurlyBillingInformationProps<T> = {
  initialState: ProcessPaymentState;
  processPaymentMutation: UseMutationResult<void, Error, T, unknown>;
  savePaymentMethodMutation?: UseMutationResult<
    void,
    Error,
    SavePaymentMethodData,
    unknown
  >;
} & (
  | {
      transactionType: 'membership';
      checkoutConfig: MembershipCheckoutConfig;
    }
  | {
      transactionType: 'donation';
      checkoutConfig: DonationCheckoutConfig;
    }
);

type FormValues = {
  postalCode: string;
};

// regex to validate us postal codes
const postalCodeRegex = /^[0-9]{5}$/;

const getValidationSchema = (t: TFunction): Yup.SchemaOf<FormValues> =>
  Yup.object({
    postalCode: Yup.string()
      .required(t('recurlyBillingInformation.inputs.postalCode.required'))
      .matches(
        postalCodeRegex,
        t('recurlyBillingInformation.inputs.postalCode.invalid'),
      ),
  });

const initialValues = {
  postalCode: '',
};

export const RecurlyBillingInformation = <
  T extends
    | ProcessMembershipPaymentTokenData
    | ProcessInitialDonationPaymentTokenData
    | ProcessDonationPaymentTokenData,
>(
  props: RecurlyBillingInformationProps<T>,
): JSX.Element => {
  const {
    transactionType,
    initialState,
    processPaymentMutation,
    savePaymentMethodMutation,
    checkoutConfig,
  } = props;
  const { t } = useTranslation();
  const recurly = useRecurly();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(getValidationSchema(t)),
    defaultValues: initialValues,
    mode: 'onBlur',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);

  const handleCardElementChange = (event: CardElementChangeEvent): void => {
    setIsCardValid(event.valid);
  };

  const isMembershipTransaction = transactionType === 'membership';
  const membershipCheckoutConfig = isMembershipTransaction
    ? checkoutConfig
    : undefined;

  const {
    email,
    firstName,
    lastName,
    clientToken,
    optInToUpdates,
    transactionAmount,
    donationRenews,
    verificationToken,
    userId,
    updatePaymentMethod,
  } = initialState;

  const { mutate, isPending } = processPaymentMutation;

  const handleSubmitError = (
    err: Error | { response: { data: (string | { email: string[] })[] } },
  ): void => {
    const transKey = parseRecurlyErrorToTransKey(err);
    if (transKey === 'common.alreadyAMember') {
      setAlreadyRegistered(true);
    } else {
      setErrors([t(transKey)]);
    }
  };

  const processMembershipPayment = (token: string): void => {
    if (!clientToken || !membershipCheckoutConfig) return;

    const data: ProcessMembershipPaymentTokenData = {
      authorizationToken: token,
      email,
      firstName,
      lastName,
      planCode: membershipCheckoutConfig.code,
      clientToken,
      optInToUpdates,
    };

    mutate(data as T, {
      onError: (err) => {
        handleSubmitError(err);
      },
    });
  };

  const processInitialDonationPayment = (token: string): void => {
    if (!clientToken) return;

    const data: ProcessInitialDonationPaymentTokenData = {
      clientToken,
      donationAmount: transactionAmount,
      donationRenews: Boolean(donationRenews),
      firstName,
      lastName,
      email,
      optInToUpdates,
      authorizationToken: token,
    };

    mutate(data as T, {
      onError: (err) => {
        handleSubmitError(err);
      },
    });
  };

  const processReturningDonationPayment = (token: string): void => {
    if (!userId || !verificationToken) return;

    const paymentData: ProcessDonationPaymentTokenData = {
      userId,
      verificationToken,
      donationAmount: transactionAmount,
      donationRenews: Boolean(donationRenews),
      authorizationToken: token,
    };

    mutate(paymentData as T, {
      onError: (err) => {
        handleSubmitError(err);
      },
    });
  };

  const savePaymentMethod = (token: string): void => {
    if (!savePaymentMethodMutation || !userId || !verificationToken) {
      return;
    }

    const data: SavePaymentMethodData = {
      userId,
      verificationToken,
      authorizationToken: token,
    };

    savePaymentMethodMutation.mutate(data, {
      onError: (err) => {
        handleSubmitError(err);
      },
    });
  };

  const submitToken = (token: string): void => {
    if (updatePaymentMethod) {
      savePaymentMethod(token);
      return;
    }

    if (isMembershipTransaction) {
      processMembershipPayment(token);
      return;
    }

    if (clientToken) {
      processInitialDonationPayment(token);
      return;
    }

    if (userId && verificationToken) {
      processReturningDonationPayment(token);
    }
  };

  const handleFormSubmit = (values: FormValues): void => {
    recurly.token(
      {
        first_name: firstName,
        last_name: lastName,
        postal_code: values.postalCode,
      },
      (err, token) => {
        if (err) {
          const validationErrors = getCreditCardValidationErrors(err, t);
          setErrors(validationErrors);
          Sentry.captureException(err);
          return;
        }

        submitToken(token.id);
      },
    );
  };

  const hasErrors = errors.length > 0;
  const isFormInvalid = !isValid || !isCardValid;

  const formattedPrice = formatToUSDollars(transactionAmount, 2);

  const checkoutSummaryPrice =
    !isMembershipTransaction && donationRenews
      ? `${formattedPrice} ${t('donate.perMonth')}`
      : formattedPrice;

  if (isPending || savePaymentMethodMutation?.isPending) {
    return <Loader disableBackground />;
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
        <Typography variant="h2">
          <b>{t('recurlyBillingInformation.title')}</b>
        </Typography>

        {alreadyRegistered && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                marginBottom: '16px',
              }}
            >
              {t('recurlyBillingInformation.registeredEmail')}
            </Typography>

            <MuiLink
              to="/login"
              component={Link}
              color="inherit"
              underline="always"
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('recurlyBillingInformation.login')}
            </MuiLink>
          </Alert>
        )}

        {hasErrors && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
              }}
            >
              {t('recurlyBillingInformation.error')}
            </Typography>
            <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
              {errors.map((error) => (
                <ListItem
                  key={error}
                  dense
                  disablePadding
                  sx={{ display: 'list-item' }}
                >
                  <ListItemText
                    primary={error}
                    sx={{ margin: 0 }}
                    slotProps={{
                      primary: {
                        variant: 'body2',
                        fontWeight: 'bold',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        <RecurlyCardElement
          error={hasErrors}
          onChange={handleCardElementChange}
        />

        <Grid container sx={{ width: '100%' }}>
          <Grid size={6}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-postalCode"
                    label={t(
                      'recurlyBillingInformation.inputs.postalCode.label',
                    )}
                    fullWidth
                    type="number"
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                    slotProps={{
                      htmlInput: { pattern: '\\d*' },
                    }}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Stack>

      <Stack spacing={2}>
        {!updatePaymentMethod && (
          <CheckoutPriceSummary
            title={checkoutConfig.title}
            price={checkoutSummaryPrice}
            description={checkoutConfig.description}
          />
        )}

        <Button fullWidth size="large" type="submit" disabled={isFormInvalid}>
          {getButtonTitle(
            isMembershipTransaction,
            Boolean(updatePaymentMethod),
            transactionAmount,
            Boolean(donationRenews),
            t,
          )}
        </Button>
      </Stack>
    </Box>
  );
};
